import { useState } from 'react';
import type { ReactNode } from 'react';
import { AuthError } from '../auth/provider';
import type { AuthProvider } from '../auth/provider';
import { useT } from '../messages';

/**
 * Credential prefill from the URL FRAGMENT — a test-era convenience the owner
 * asked for (a launchpad page whose links arrive signed-in-ready).
 *
 * The fragment, never the query string, and that is the entire design: a query
 * string is sent to the server and would persist a password verbatim in the
 * load balancer's and Cloud Run's request logs — the exact class of leak the
 * logging redaction discipline exists to prevent. A fragment never leaves the
 * browser. It is read once, the address bar is scrubbed immediately
 * (`history.replaceState`) so the secret does not sit in the URL bar or land
 * in browser history, and nothing auto-submits — the person still presses the
 * button. Absent or malformed, the form is exactly what it always was.
 */
function readFragmentPrefill(): { email: string; password: string } {
  try {
    const raw = window.location.hash;
    if (!raw.includes('email=')) return { email: '', password: '' };
    const params = new URLSearchParams(raw.replace(/^#/, ''));
    const email = params.get('email') ?? '';
    const password = params.get('password') ?? '';
    if (email !== '' || password !== '') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    return { email, password };
  } catch {
    return { email: '', password: '' };
  }
}

/**
 * Sign in.
 *
 * The dev banner is not decoration. `VITE_AUTH_FAKE` mints unsigned tokens and
 * the whole point of `core-api`'s `FakeVerifier` refusing to exist under
 * `NODE_ENV=production` is that the two modes must never be confusable. A screen
 * that looked identical either way would undo that on the other side of the
 * wire, so this one says which it is.
 */
export function SignIn({ auth }: { auth: AuthProvider }): ReactNode {
  const t = useT();
  const [prefill] = useState(readFragmentPrefill);
  const [email, setEmail] = useState(prefill.email);
  const [password, setPassword] = useState(prefill.password);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await auth.signIn(email, password);
    } catch (e) {
      setError(
        e instanceof AuthError && e.reason === 'unavailable'
          ? t('signIn.errorUnavailable')
          : t('signIn.errorMismatch'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <form
        className="signin__panel"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {/* The product's own name, which is not a word in any language. */}
        <h1>yourstore</h1>
        {auth.kind === 'fake' ? (
          <p className="signin__dev">
            {t('signIn.devLead')}
            <code>core-api</code>
            {t('signIn.devMid')}
            <code>AUTH_FAKE_TOKENS=1</code>.
          </p>
        ) : null}

        <label className="field">
          <span>{t('signIn.email')}</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>

        {auth.kind === 'firebase' ? (
          <label className="field">
            <span>{t('signIn.password')}</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
        ) : null}

        {error !== null ? <p className="error">{error}</p> : null}

        <button type="submit" className="button button--primary" disabled={busy}>
          {busy ? t('signIn.signingIn') : t('signIn.submit')}
        </button>
      </form>
    </main>
  );
}
